/**
 * Asset subtitles (1.9.0): structured cues in source time, shown and typed in the file's own timecode, with the
 * track's default style and each cue's own (placement grid, colour, background, size; italic and bold in the text).
 * The draft lives above the tab (subtitles/draftStore.ts): switching tabs keeps unsaved edits, another person's save
 * does not remount the editor ("Changed by X: Reload / Keep mine"), and leaving the asset with unsaved edits asks first
 * (Asset.tsx). SRT, WebVTT and TTML import (with the programme-timecode offer); SRT, WebVTT, TTML IMSC 1.1 and EBU-TT-D
 * download of the saved track. The subtitle keys (subtitles/keys.ts) work while the tab is open.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Bold, FileDown, FileUp, Italic, Mic, Plus, RotateCcw, Save, Trash2 } from 'lucide-react';
import {
  DEFAULT_SUBTITLE_RULES, SIDECAR_FORMATS, SIDECAR_LABELS, applyCueStyle, effectiveCueStyle, legacyAlign, newCueAt, parseSubtitles, programmeCuesPastMidnight, programmeOffset, secondsToTicks,
  setCueEdge, shiftCues, ticksToSeconds, type AssetDetail, type Cue, type CueAlign, type CueEditContext, type CueStyle, type CueStylePatch,
} from '@prism/contracts';
import { api, ApiError, errorMessage } from '../api/client';
import { useMe, useSubtitles, useTranscriptions, type SubtitleTrackDTO, type TranscriptionDTO } from '../api/hooks';
import { Modal } from '../components/Modal';
import { toast } from '../components/Toasts';
import { ErrorBanner } from '../components/ErrorBanner';
import { relative } from '../lib/format';
import { useFollowPlayhead } from '../lib/followList';
import { FollowButton } from '../components/FollowButton';
import { usePref } from '../lib/prefs';
import { queryKeys } from '../api/queryKeys';
import {
  CueRow, PlacementGrid, Popover, StyleFields, TrackStyleEditor, clickSelection, cueKeyResult, cueTimecode, discardDraft, draftFrom, editDraft, focusCueText, focusedCueCaret,
  getCueSelection, getDraft, keepMine, parseSignedTimecode, savedDraft, selectTrack, selectedTrack, setCueSelection, setDraft, signedTimecode, toggleInlineTag, useDraftStore,
  useSubtitleKeys, SUBTITLE_IMPORT_ACCEPT, importNameParts, trackMetrics, type CueEdge, type SubtitleDraft,
} from '../subtitles';

const LANGUAGES = [
  { value: 'auto', label: 'Auto-detect' },
  { value: 'fi', label: 'Finnish' },
  { value: 'sv', label: 'Swedish' },
  { value: 'en', label: 'English' },
] as const;
const languageName = (code: string) => (code === 'auto' ? 'auto-detect' : LANGUAGES.find((l) => l.value === code)?.label ?? code);
const isActive = (t: TranscriptionDTO | undefined): boolean => t?.status === 'queued' || t?.status === 'running';

/** The live player, read at the moment of a key press (playerTime is throttled). */
export interface SubtitlePlayer {
  /** The playhead in seconds of media time. */
  now(): number;
  seek(seconds: number): void;
  playRange(fromSeconds: number, toSeconds: number): void;
}

export function SubtitlesTab({ asset, player, currentSeconds, playing = false, rate, startFrames }: {
  asset: AssetDetail; player: SubtitlePlayer; currentSeconds: number;
  /** 1.9.3: whether the player is playing (Follow: a play ends a pause made by scrolling by hand). */
  playing?: boolean;
  /** The media's rate and start timecode, as the Player has them (Asset.tsx). */
  rate: { num: number; den: number }; startFrames: number;
}) {
  const me = useMe().data!;
  const q = useSubtitles(asset.id);
  const qc = useQueryClient();
  useDraftStore();
  const canEdit = me.permissions.includes('asset.edit_metadata') && !asset.deleted;
  const tracks = q.data?.items ?? [];
  const chosen = selectedTrack(asset.id);
  const track = tracks.find((t) => t.id === chosen) ?? tracks[0] ?? null;
  const hasAudio = asset.technical?.streams.some((s) => s.type === 'audio') ?? false;
  const transcriptions = useTranscriptions(asset.id);
  const latest = transcriptions.data?.items[0];
  const [generating, setGenerating] = useState(false);
  const [offer, setOffer] = useState<{ trackId: string; offset: number } | null>(null);
  const [notes, setNotes] = useState<{ trackId: string; notes: string[] } | null>(null);

  // When a request this person watched finishes, open the new track in the editor.
  const seen = useRef<{ id: string; status: string } | null>(null);
  useEffect(() => {
    if (!latest) return;
    const prev = seen.current;
    if (prev && prev.id === latest.id && (prev.status === 'queued' || prev.status === 'running') && latest.status === 'succeeded' && latest.trackId) {
      selectTrack(asset.id, latest.trackId);
      toast(`Subtitles generated: ${latest.cueCount} cues. Check them before you use them.`);
    }
    seen.current = { id: latest.id, status: latest.status };
  }, [latest?.id, latest?.status, latest?.trackId, latest?.cueCount, asset.id]);

  async function create(importFile?: File) {
    try {
      let body: Record<string, unknown>;
      if (importFile) {
        const content = await importFile.text();
        const name = importNameParts(importFile.name);
        // The language from the name (".fi.srt"), else from the file (TTML xml:lang). When neither tells it, it is
        // left out and the server decides (the file's xml:lang, the name, else und).
        let fileLanguage: string | undefined;
        try { fileLanguage = parseSubtitles(content).language; } catch { /* the server says what is wrong */ }
        const language = name.language ?? fileLanguage;
        body = { ...(language ? { language } : {}), label: name.label, import: { content, filename: importFile.name } };
      } else {
        body = { language: 'en', label: 'English', cues: [] };
      }
      const res = await api<SubtitleTrackDTO & { importWarnings: unknown[]; importNotes?: string[] }>(`/api/v1/assets/${asset.id}/subtitles`, { method: 'POST', json: body });
      await qc.invalidateQueries({ queryKey: queryKeys.subtitles(asset.id) });
      selectTrack(asset.id, res.id);
      toast(importFile ? `Imported ${res.cues.length} cues` : 'Subtitle track created');
      // A broadcaster's file in programme timecode (10:00:00:00 …): offer to move it onto the media.
      if (importFile) {
        // What the file had that could not be kept (TTML: custom regions, fonts, colours of words).
        setNotes(res.importNotes?.length ? { trackId: res.id, notes: res.importNotes } : null);
        const offset = programmeOffset(res.cues, startFrames, rate, asset.durationTicks ?? 0);
        setOffer(offset > 0 ? { trackId: res.id, offset } : null);
      }
    } catch (err) {
      toast(errorMessage(err), 'error');
    }
  }

  return (
    <div data-testid="subtitles-tab" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
        {tracks.map((t) => {
          const d = getDraft(asset.id, t.id);
          return <button key={t.id} className={`tag toggle ${track?.id === t.id ? 'on' : ''}`} onClick={() => selectTrack(asset.id, t.id)} data-testid="subtitle-track">{t.label} · {t.language}{d?.dirty ? ' •' : ''}</button>;
        })}
        <span style={{ flex: 1 }} />
        {canEdit && (
          <>
            {hasAudio && (
              <button className="btn ghost sm" disabled={isActive(latest)} title={isActive(latest) ? 'Subtitles are being generated' : 'Make subtitles from the audio'} onClick={() => setGenerating(true)} data-testid="generate-subtitles"><Mic size={13} /> Generate subtitles…</button>
            )}
            <button className="btn ghost sm" onClick={() => void create()}><Plus size={13} /> New track</button>
            <label className="btn ghost sm" style={{ cursor: 'pointer' }} data-testid="import-subtitles" title="SRT, WebVTT or TTML (IMSC, EBU-TT-D, DFXP)"><FileUp size={13} /> Import…
              <input type="file" accept={SUBTITLE_IMPORT_ACCEPT} hidden data-testid="import-subtitles-file" onChange={(e) => { const f = e.target.files?.[0]; if (f) void create(f); e.target.value = ''; }} />
            </label>
          </>
        )}
      </div>
      <GenerationStatus assetId={asset.id} latest={latest} canEdit={canEdit} />
      {!tracks.length && !q.isLoading && <p className="muted" style={{ margin: 0 }}>No subtitles for this version yet. {canEdit && hasAudio ? 'Generate them from the audio, import' : 'Import'} an SRT, WebVTT or TTML file, or create a track and add cues while you watch.</p>}
      {track && notes?.trackId === track.id && (
        <div className="banner info" data-testid="import-notes">
          <div className="banner-body" style={{ display: 'flex', gap: 8, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <div style={{ flex: 1 }}>
              <span>Imported. Some of the file could not be kept:</span>
              <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>{notes.notes.slice(0, 10).map((n, i) => <li key={i}>{n}</li>)}</ul>
              {notes.notes.length > 10 && <span className="muted">and {notes.notes.length - 10} more</span>}
            </div>
            <button className="btn ghost sm" onClick={() => setNotes(null)} data-testid="import-notes-dismiss">Dismiss</button>
          </div>
        </div>
      )}
      {track && offer?.trackId === track.id && (
        <div className="banner info" data-testid="programme-offer">
          <div className="banner-body" style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ flex: 1 }}>The times in this file look like programme timecode: they start {signedTimecode(offer.offset, rate).slice(1)} after the media’s start timecode would put them.</span>
            {canEdit && <button className="btn sm" data-testid="programme-shift" onClick={() => {
              const d = getDraft(asset.id, track.id) ?? draftFrom(asset.id, track);
              // 1.9.1: cues after midnight (00:10 in a file from 23:30) are on the next day.
              setDraft(editDraft(d, { cues: shiftCues(programmeCuesPastMidnight(d.cues, startFrames, rate, asset.durationTicks ?? 0), -offer.offset) }));
              setOffer(null);
              toast('Shifted to the media’s timecode. Save to keep it.');
            }}>Shift to the media’s timecode</button>}
            <button className="btn ghost sm" onClick={() => setOffer(null)}>Keep as is</button>
          </div>
        </div>
      )}
      {track && <TrackEditor track={track} asset={asset} canEdit={canEdit} player={player} currentSeconds={currentSeconds} playing={playing} rate={rate} startFrames={startFrames} />}
      {generating && <GenerateDialog assetId={asset.id} onClose={() => setGenerating(false)} />}
    </div>
  );
}

/** Asks the server to make subtitles from the audio. Errors are returned as text for the caller to show. */
async function requestGeneration(assetId: string, language: string, qc: ReturnType<typeof useQueryClient>): Promise<string | null> {
  try {
    await api(`/api/v1/assets/${assetId}/subtitles/generate`, { method: 'POST', json: { language } });
    await qc.invalidateQueries({ queryKey: queryKeys.subtitles(assetId) });
    return null;
  } catch (err) {
    const e = err as ApiError;
    return e.code === 'SPEECH_UNAVAILABLE' ? `The speech service is not available. ${e.message}` : e.message;
  }
}

function GenerateDialog({ assetId, onClose }: { assetId: string; onClose: () => void }) {
  const qc = useQueryClient();
  const [language, setLanguage] = useState<string>('auto');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function start() {
    setBusy(true);
    setError(null);
    const problem = await requestGeneration(assetId, language, qc);
    if (problem) { setError(problem); setBusy(false); return; }
    toast('Generating subtitles. You can keep working; the track appears here when it is ready.');
    onClose();
  }
  return (
    <Modal title="Generate subtitles" onClose={onClose} actions={<><button className="btn ghost" onClick={onClose}>Cancel</button><button className="btn primary" disabled={busy} onClick={() => void start()} data-testid="start-generate">Generate</button></>}>
      <div className="field">
        <label htmlFor="gen-lang">Language</label>
        <select id="gen-lang" className="select" value={language} onChange={(e) => setLanguage(e.target.value)} data-testid="generate-language">
          {LANGUAGES.map((l) => <option key={l.value} value={l.value}>{l.label}</option>)}
        </select>
      </div>
      <p className="muted" style={{ marginBottom: 0 }}>Runs on the server; takes about as long as the clip. You can edit the result.</p>
      {error && <ErrorBanner alert style={{ marginTop: 12 }}><span>{error}</span></ErrorBanner>}
    </Modal>
  );
}

/** While subtitles are being generated: what is happening and a way to stop it; after a failure: why, and Retry. */
function GenerationStatus({ assetId, latest, canEdit }: { assetId: string; latest: TranscriptionDTO | undefined; canEdit: boolean }) {
  const qc = useQueryClient();
  const [, tick] = useState(0);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!isActive(latest)) return;
    const t = setInterval(() => tick((n) => n + 1), 30_000); // keeps "started 3 minutes ago" fresh
    return () => clearInterval(t);
  }, [latest?.id, latest?.status]);
  if (!latest) return null;

  if (latest.status === 'queued' || latest.status === 'running') {
    return (
      <div className="banner info" role="status" data-testid="generation-status">
        <div className="banner-body">
          <strong>Generating subtitles ({languageName(latest.language)})…</strong>
          <span className="muted">{latest.status === 'queued' ? 'Waiting for a worker' : `Started ${relative(latest.startedAt ?? latest.createdAt)}`}{latest.requestedBy ? ` · requested by ${latest.requestedBy}` : ''}. It takes about as long as the clip.</span>
          {canEdit && (
            <div className="actions">
              <button className="btn ghost sm" disabled={busy} data-testid="cancel-generation" onClick={async () => {
                setBusy(true);
                try {
                  await api(`/api/v1/assets/${assetId}/transcriptions/${latest.id}/cancel`, { method: 'POST', json: {} });
                  await qc.invalidateQueries({ queryKey: queryKeys.subtitles(assetId) });
                } catch (err) {
                  toast(errorMessage(err), 'error');
                } finally { setBusy(false); }
              }}>Cancel</button>
            </div>
          )}
        </div>
      </div>
    );
  }
  // A failure stays visible for a day, until there is a newer request.
  if (latest.status === 'failed' && latest.error && Date.now() - new Date(latest.finishedAt ?? latest.createdAt).getTime() < 24 * 3600_000) {
    return (
      <ErrorBanner alert testId="generation-failed" title="Subtitles could not be generated">
        <span>{latest.error.message}</span>
        {latest.error.hint && <span className="muted">{latest.error.hint}</span>}
        {canEdit && (
          <div className="actions">
            <button className="btn sm" disabled={busy} data-testid="retry-generation" onClick={async () => {
              setBusy(true);
              const problem = await requestGeneration(assetId, latest.language, qc);
              if (problem) toast(problem, 'error'); else toast('Generating subtitles again');
              setBusy(false);
            }}><RotateCcw size={13} /> Retry</button>
          </div>
        )}
      </ErrorBanner>
    );
  }
  return null;
}

/** The cues of the selected cues' common style field, or undefined when they differ. */
function shared<K extends keyof CueStyle>(cues: Cue[], key: K): CueStyle[K] | undefined {
  const vals = cues.map((c) => c.style?.[key]);
  return vals.length && vals.every((v) => v === vals[0]) ? vals[0] : undefined;
}

function TrackEditor({ track, asset, canEdit, player, currentSeconds, playing, rate, startFrames }: {
  track: SubtitleTrackDTO; asset: AssetDetail; canEdit: boolean; player: SubtitlePlayer; currentSeconds: number; playing: boolean; rate: { num: number; den: number }; startFrames: number;
}) {
  const qc = useQueryClient();
  const draft: SubtitleDraft = getDraft(asset.id, track.id) ?? draftFrom(asset.id, track);
  const sel = getCueSelection(asset.id, track.id);
  const [shift, setShift] = useState('+00:00:00:00');
  const [conflict, setConflict] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const durationTicks = asset.durationTicks ?? undefined;
  const ctx: CueEditContext = useMemo(() => ({ frameRate: rate, durationTicks: durationTicks ?? null, trackStyle: draft.style }), [rate, durationTicks, draft.style]);
  const metrics = useMemo(() => trackMetrics(draft.cues, draft.style, durationTicks), [draft.cues, draft.style, durationTicks]);
  const sorted = useMemo(() => [...draft.cues].sort((a, b) => a.startTicks - b.startTicks), [draft.cues]);
  const errors = [...metrics.values()].reduce((n, m) => n + m.issues.filter((i) => i.severity === 'error').length, 0);
  const warnings = [...metrics.values()].reduce((n, m) => n + m.issues.filter((i) => i.severity !== 'error').length, 0);
  const now = secondsToTicks(currentSeconds);
  // 1.9.3: every cue at the playhead is marked (a top and a bottom cue can show at once), and Follow keeps the first
  // in view as it plays, whether or not cues are selected (before, it scrolled only while none was selected).
  const atPlayhead = useMemo(() => new Set(sorted.filter((c) => now >= c.startTicks && now < c.endTicks).map((c) => c.id)), [sorted, now]);
  const followId = sorted.find((c) => atPlayhead.has(c.id))?.id ?? null;
  const [follow, setFollow] = usePref('asset.subtitles.follow', false);
  const cueList = useRef<HTMLDivElement>(null);
  useFollowPlayhead({ enabled: follow, playing, targetId: followId, list: cueList, attr: 'data-cue' });
  const selectedCues = draft.cues.filter((c) => sel.ids.includes(c.id));
  const trackLook = effectiveCueStyle(draft.style, { text: '' });

  const update = (patch: Partial<Pick<SubtitleDraft, 'cues' | 'style' | 'label' | 'language'>>) => {
    if (!canEdit) return;
    setDraft(editDraft(getDraft(asset.id, track.id) ?? draft, patch));
  };
  const setCues = (fn: (cues: Cue[]) => Cue[]) => {
    const cur = getDraft(asset.id, track.id) ?? draft;
    const next = fn(cur.cues);
    if (next !== cur.cues) update({ cues: next });
  };
  const select = (ids: string[], edge: CueEdge | null = null) => setCueSelection(asset.id, track.id, { ids, edge });
  const styleSelected = (patch: CueStylePatch | null, ids = sel.ids) => setCues((cs) => applyCueStyle(cs, ids, patch));

  async function save() {
    setSaving(true);
    try {
      const cur = getDraft(asset.id, track.id) ?? draft;
      const res = await api<SubtitleTrackDTO>(`/api/v1/subtitle-tracks/${track.id}`, { method: 'PUT', json: { revision: cur.baseRevision, cues: cur.cues, label: cur.label, language: cur.language, style: cur.style } });
      setDraft(savedDraft(cur, res));
      setConflict(null);
      qc.setQueryData<{ items: SubtitleTrackDTO[] }>(queryKeys.subtitles(asset.id), (old) => old ? { items: old.items.map((t) => (t.id === res.id ? res : t)) } : old);
      await qc.invalidateQueries({ queryKey: queryKeys.subtitles(asset.id) });
      toast('Subtitles saved');
    } catch (err) {
      const e = err as ApiError;
      if (e instanceof ApiError && e.code === 'REVISION_CONFLICT') {
        const current = Number(e.details?.currentRevision);
        setConflict(Number.isFinite(current) ? current : null);
        await qc.invalidateQueries({ queryKey: queryKeys.subtitles(asset.id) });
      } else toast(errorMessage(err), 'error');
    } finally { setSaving(false); }
  }
  async function reload() {
    discardDraft(asset.id, track.id);
    select([]);
    setConflict(null);
    await qc.invalidateQueries({ queryKey: queryKeys.subtitles(asset.id) });
  }
  function keep(revision: number) {
    setDraft(keepMine(getDraft(asset.id, track.id) ?? draft, revision));
    setConflict(null);
  }
  async function remove() {
    if (!window.confirm(`Delete the ${track.label} track?`)) return;
    try {
      await api(`/api/v1/subtitle-tracks/${track.id}?revision=${track.revision}`, { method: 'DELETE' });
      discardDraft(asset.id, track.id);
      selectTrack(asset.id, null);
      await qc.invalidateQueries({ queryKey: queryKeys.subtitles(asset.id) });
    } catch (err) { toast(errorMessage(err), 'error'); }
  }
  function addCue() {
    const r = newCueAt(draft.cues, secondsToTicks(player.now()), ctx);
    if (!r) { toast('No room for a cue here: the next cue starts too soon', 'error'); return; }
    update({ cues: r.cues });
    select([r.cue.id]);
    focusCueText(r.cue.id);
  }
  /** ⌘I / ⌘B from the toolbar: the words selected in the cue text that has focus, else the selected cues' whole text. */
  function inline(tag: 'i' | 'b') {
    const el = document.activeElement;
    if (el instanceof HTMLTextAreaElement && el.dataset.cueText) {
      const id = el.dataset.cueText;
      const r = toggleInlineTag(el.value, el.selectionStart, el.selectionEnd, tag);
      setCues((cs) => cs.map((c) => (c.id === id ? { ...c, text: r.text } : c)));
      requestAnimationFrame(() => { try { el.setSelectionRange(r.start, r.end); } catch { /* gone */ } });
      return;
    }
    const ids = new Set(sel.ids);
    setCues((cs) => cs.map((c) => (ids.has(c.id) ? { ...c, text: toggleInlineTag(c.text, 0, 0, tag).text } : c)));
  }

  useSubtitleKeys(true, (action) => {
    const cur = getDraft(asset.id, track.id) ?? draft;
    const edits = action.action !== 'play-cue' && action.action !== 'previous-cue' && action.action !== 'next-cue';
    if (edits && !canEdit) return false;
    const r = cueKeyResult(action, { cues: cur.cues, selection: getCueSelection(asset.id, track.id), playhead: secondsToTicks(player.now()), caret: focusedCueCaret(sel.ids[0]), ctx });
    if (!r) return false;
    if (r.cues) update({ cues: r.cues });
    if (r.selection) select(r.selection.ids, r.selection.edge);
    if (r.seek !== undefined) player.seek(ticksToSeconds(r.seek));
    if (r.play) player.playRange(ticksToSeconds(r.play.from), ticksToSeconds(r.play.to));
    if (r.focus) focusCueText(r.focus);
    else if (r.selection && action.action !== 'nudge') requestAnimationFrame(() => document.querySelector(`[data-testid="subtitles-tab"] [data-cue="${r.selection!.ids[0]}"]`)?.scrollIntoView({ block: 'nearest' }));
    return true;
  });

  const shiftTicks = parseSignedTimecode(shift, rate);
  const alignOf = (c: Cue): CueAlign | null => c.style?.align ?? legacyAlign(c.text) ?? null;
  const gridValue = selectedCues.length && selectedCues.every((c) => alignOf(c) === alignOf(selectedCues[0]!)) ? alignOf(selectedCues[0]!) : null;
  const gridMixed = selectedCues.length > 1 && !selectedCues.every((c) => alignOf(c) === alignOf(selectedCues[0]!));
  const sharedStyle: CueStyle = { color: shared(selectedCues, 'color'), background: shared(selectedCues, 'background'), size: shared(selectedCues, 'size') };
  const none = !canEdit || !selectedCues.length;
  const changedBy = draft.serverChange ? (draft.serverChange.by ?? 'someone else') : null;

  return (
    <div className="inspector-pane" data-testid="subtitle-track-editor">
      {(draft.serverChange || conflict !== null) && (
        <div className="banner warning" data-testid="subtitle-conflict">
          <div className="banner-body" style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ flex: 1 }}>{conflict !== null && !draft.serverChange ? 'These subtitles were changed elsewhere while you edited them.' : `Changed by ${changedBy} while you edited.`} Reload to see theirs (your changes go), or keep yours and save over them.</span>
            <button className="btn sm" onClick={() => void reload()} data-testid="subtitle-reload">Reload</button>
            <button className="btn ghost sm" onClick={() => keep(draft.serverChange?.revision ?? conflict ?? draft.baseRevision)} data-testid="subtitle-keep-mine">Keep mine</button>
          </div>
        </div>
      )}
      <div className="field-row">
        <div className="field"><label htmlFor="st-label">Label</label><input id="st-label" className="input" value={draft.label} disabled={!canEdit} onChange={(e) => update({ label: e.target.value })} /></div>
        <div className="field"><label htmlFor="st-lang">Language (BCP 47)</label><input id="st-lang" className="input" value={draft.language} disabled={!canEdit} onChange={(e) => update({ language: e.target.value })} /></div>
      </div>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
        <span className={errors ? 'error-text' : 'faint'} style={{ fontSize: 12 }} data-testid="subtitle-summary">{draft.cues.length} cues · {errors} errors · {warnings} warnings</span>
        <span style={{ flex: 1 }} />
        <FollowButton on={follow} onChange={setFollow} testId="subtitle-follow" />
        <Popover label="Download the track" button={<><FileDown size={13} /> Download</>} testId="subtitle-download" width={250}>
          {(close) => (
            <div className="sub-menu" role="group" aria-label="Download">
              <p className="faint" style={{ margin: '0 0 6px', fontSize: 12 }}>{draft.dirty ? 'Downloads the saved track: save first to include your changes.' : 'Downloads the saved track.'}</p>
              {SIDECAR_FORMATS.map((f) => (
                <a key={f} className="row-menu-item" href={`/api/v1/subtitle-tracks/${track.id}/export?format=${f}`} data-format={f} data-testid={`subtitle-download-${f}`} onClick={() => close()}>{SIDECAR_LABELS[f]}</a>
              ))}
              <p className="faint" style={{ margin: '6px 0 0', fontSize: 11 }}>SRT and WebVTT keep the placement and colour; background and size are kept in the TTML files only.</p>
            </div>
          )}
        </Popover>
        {canEdit && <button className="btn ghost sm danger" aria-label="Delete track" title="Delete track" onClick={() => void remove()}><Trash2 size={13} /></button>}
      </div>
      {canEdit && (
        <div className="sub-toolbar" role="toolbar" aria-label="Cue style" data-testid="subtitle-toolbar">
          <button type="button" className="btn ghost sm icon" title="Italic (⌘I): the selected words, or the selected cues" aria-label="Italic" disabled={none}
            onMouseDown={(e) => e.preventDefault()} onClick={() => inline('i')} data-testid="toolbar-italic"><Italic size={13} /></button>
          <button type="button" className="btn ghost sm icon" title="Bold (⌘B): the selected words, or the selected cues" aria-label="Bold" disabled={none}
            onMouseDown={(e) => e.preventDefault()} onClick={() => inline('b')} data-testid="toolbar-bold"><Bold size={13} /></button>
          <span className="sub-toolbar-sep" />
          <Popover label="Colour" button={<><span className="style-dot" style={{ background: sharedStyle.color ?? trackLook.color }} aria-hidden /> Colour</>} disabled={none} testId="toolbar-color" width={250}>
            {() => <StyleFields only="color" value={sharedStyle} fallback={trackLook} testId="toolbar-color" onChange={(p) => styleSelected(p)} />}
          </Popover>
          <Popover label="Background" button={<>Background</>} disabled={none} testId="toolbar-background" width={250}>
            {() => <StyleFields only="background" value={sharedStyle} fallback={trackLook} testId="toolbar-background" onChange={(p) => styleSelected(p)} />}
          </Popover>
          <Popover label="Size" button={<>Size</>} disabled={none} testId="toolbar-size" width={290}>
            {() => <StyleFields only="size" value={sharedStyle} fallback={trackLook} testId="toolbar-size" onChange={(p) => styleSelected(p)} />}
          </Popover>
          <PlacementGrid value={gridValue} mixed={gridMixed} trackAlign={trackLook.align} disabled={none} testId="toolbar-grid" onChange={(a) => styleSelected({ align: a })} />
          <span style={{ flex: 1 }} />
          <TrackStyleEditor value={draft.style} onChange={(s) => update({ style: s ?? {} })} testId="track-style" />
        </div>
      )}
      {canEdit && (
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
          <span className="faint" style={{ fontSize: 12 }}>Shift all cues by</span>
          <input className={`input mono ${shiftTicks == null ? 'invalid' : ''}`} style={{ width: 132, height: 28 }} value={shift} onChange={(e) => setShift(e.target.value)} aria-label="Shift by timecode"
            title="A signed timecode: −00:00:01:12 moves every cue earlier by a second and 12 frames" data-testid="subtitle-shift" />
          <button className="btn sm" disabled={!shiftTicks} onClick={() => { if (shiftTicks) { setCues((cs) => shiftCues(cs, shiftTicks)); toast(`Shifted ${draft.cues.length} cues by ${signedTimecode(shiftTicks, rate)}`); } }} data-testid="subtitle-shift-apply">Apply</button>
        </div>
      )}
      <div className="cue-list" style={{ maxHeight: 460, overflow: 'auto' }} data-testid="cue-list" aria-multiselectable ref={cueList}>
        {sorted.map((c, i) => (
          <CueRow key={c.id} cue={c} index={i} rate={rate} startFrames={startFrames} durationTicks={asset.durationTicks} trackStyle={draft.style} metrics={metrics.get(c.id)!} rules={DEFAULT_SUBTITLE_RULES}
            selected={sel.ids.includes(c.id)} atPlayhead={atPlayhead.has(c.id)} activeEdge={sel.ids.length === 1 && sel.ids[0] === c.id ? sel.edge : null} readOnly={!canEdit}
            onSelect={({ extend }) => {
              const next = clickSelection(getCueSelection(asset.id, track.id), c.id, extend);
              select(next.ids, next.edge);
              if (!extend) player.seek(ticksToSeconds(c.startTicks));
            }}
            onEdge={(edge, t) => { setCues((cs) => setCueEdge(cs, c.id, edge, t, ctx)); select([c.id], edge); }}
            onActiveEdge={(edge) => select([c.id], edge)}
            onText={(text) => setCues((cs) => cs.map((x) => (x.id === c.id ? { ...x, text } : x)))}
            onStyle={(patch) => styleSelected(patch, sel.ids.includes(c.id) ? sel.ids : [c.id])}
            onDelete={() => { setCues((cs) => cs.filter((x) => x.id !== c.id)); select(sel.ids.filter((x) => x !== c.id)); }} />
        ))}
      </div>
      {canEdit && (
        <div className="form-footer" style={{ position: 'static', margin: 0, padding: 0, background: 'none' }}>
          <button className="btn sm" onClick={addCue} data-testid="add-cue" title="A 3 s cue at the playhead (⌥N)"><Plus size={13} /> Cue at {cueTimecode(now, rate, startFrames)}</button>
          <span style={{ flex: 1 }} />
          {draft.dirty && <span className="faint" style={{ fontSize: 12 }} data-testid="subtitle-unsaved">Unsaved changes</span>}
          {draft.dirty && <button className="btn ghost sm" onClick={() => { if (window.confirm('Discard your changes to this track?')) void reload(); }} data-testid="subtitle-discard"><RotateCcw size={13} /> Discard</button>}
          <button className="btn primary sm" disabled={!draft.dirty || saving} onClick={() => void save()} data-testid="save-subtitles"><Save size={13} /> Save</button>
        </div>
      )}
    </div>
  );
}
